import { useEffect, useRef } from "react";
import type { UseFormSetValue } from "react-hook-form";
import { deriveActualEnd, type AppointmentFormValues } from "./appointmentForm.schema";

/**
 * Ist-Ende follows the Ist times, until somebody types their own.
 *
 * `actualEndsAt` is the one Ist field that is **not** part of the occupancy COALESCE, so nothing
 * else keeps it honest: move a completed Termin on the board and its Ist start and Ist durations
 * are rewritten while the sign-off time sits where it was — eventually *before* the start it is
 * supposed to follow. Deriving it closes that gap without turning it into a computed field, which
 * it must not become: `PATCH :id/complete` records what the Handwerker actually reported, and a
 * genuine 15:10 sign-off on a job whose durations round to 15:00 is data, not a rounding error.
 *
 * The three rules, in the order the effect applies them — the same shape as `useFullDaySync`, for
 * the same reason:
 *
 * 1. **First look after a seed → adopt, never derive.** A stored `actualEndsAt` that disagrees with
 *    the durations is exactly the case above, and re-deriving on open would delete it before the
 *    user had seen it — and mark a form dirty that nobody touched.
 * 2. **A hand edit stops the derivation, for good.** Anything in the field that this hook did not
 *    write came from the user, and from then on the value is theirs. There is no un-editing it back
 *    into automatic: a field that silently resumed overwriting itself later would be worse than one
 *    that simply stays put.
 * 3. **Only a change to the Ist times derives.** Keyed on the start and the work duration — the two
 *    inputs of `deriveActualEnd` — so an unrelated render cannot walk the value forward, and rule 1
 *    survives every re-render rather than only the first.
 *
 * Not wired for Aufgaben: `UpdateTaskDtoSchema` has no `actualEnd` at all, and the TASK branch of
 * `UpdateScheduleEntryUseCase` drops `actualEndsAt`. There is nowhere to send it.
 */

interface Params {
  /**
   * The modal is open, seeded, and the status is one `hasActuals` covers. Nothing is derived for a
   * Termin whose Ist-Zeiten are not even on screen — and flipping the status on is what makes this
   * take its first-look baseline, so the field is adopted rather than rewritten.
   */
  enabled: boolean;
  actualStart: string;
  actualWorkDuration: number;
  actualEnd: string;
  setValue: UseFormSetValue<AppointmentFormValues>;
}

export function useActualEndSync({
  enabled,
  actualStart,
  actualWorkDuration,
  actualEnd,
  setValue,
}: Params): void {
  const initialized = useRef(false);
  /** Exactly what this hook last wrote. Anything else in the field is the user's. */
  const baseline = useRef<string>("");
  const manual = useRef(false);
  const lastKey = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      initialized.current = false;
      baseline.current = "";
      manual.current = false;
      lastKey.current = null;
      return;
    }

    const key = `${actualStart}|${actualWorkDuration}`;

    // Rule 1.
    if (!initialized.current) {
      initialized.current = true;
      baseline.current = actualEnd;
      lastKey.current = key;
      return;
    }

    // Rule 2. Checked before the key comparison: a keystroke in Ist-Ende changes no key at all, so
    // testing the key first would let every hand edit through unnoticed.
    if (actualEnd !== baseline.current) {
      manual.current = true;
      baseline.current = actualEnd;
    }

    // Rule 3.
    if (key === lastKey.current) return;
    lastKey.current = key;

    if (manual.current) return;

    const next = deriveActualEnd(actualStart, actualWorkDuration);
    // A half-typed start or an emptied Arbeitszeit derives nothing. Leaving the last good value in
    // place beats blanking a field mid-keystroke — the same rule `toSlot` applies to the block.
    if (!next || next === actualEnd) return;

    setValue("actualEnd", next, { shouldDirty: true });
    baseline.current = next;
  }, [enabled, actualStart, actualWorkDuration, actualEnd, setValue]);
}
